import React, { useState, useEffect, useMemo } from 'react';
import { createMilestone } from '../firebase/milestones.service';
import { createAction } from '../firebase/actions.service';
import { addMilestoneToSession } from '../firebase/sparring.service';
import { logMilestoneCreated } from '../firebase/changelog.service';
import { SparringMessage } from '../types';
import { SuccessIcon, TipIcon } from '../../../icons';

interface SparringMilestoneCaptureProps {
  goalId: string;
  ownerId: string;
  sessionId: string;
  currentMilestoneCount: number;
  messages?: SparringMessage[]; // Conversation messages to extract answers from
  onMilestoneCreated: (milestoneId: string) => void;
  onCancel: () => void;
  onFinish?: () => void; // Called when user is done adding milestones
}

// Geen max limiet meer - dynamisch aantal mijlpalen gebaseerd op doelduur
// Behoud export voor backwards compatibility
export const MAX_MILESTONES = 999;

// Milestone templates aligned with the structured 7-step action plan
const MILESTONE_TEMPLATES = [
  {
    key: 'einddoel',
    label: 'Einddoel',
    description: 'Je ultieme bestemming – het doel dat op termijn behaald moet worden.',
    searchTerms: ['einddoel', 'ultieme doel', 'succes'],
    aliases: ['einddoel', '🎯 einddoel', 'het einddoel'],
  },
  {
    key: 'waarom',
    label: 'Waarom',
    description: 'De betekenis achter je doel – wat je drijft om dit te doen.',
    searchTerms: ['waarom', 'motivatie', 'drijfveer'],
    aliases: ['waarom', '💡 waarom', 'de reden waarom'],
  },
  {
    key: '1jaar',
    label: 'Over 1 jaar',
    description: 'Een meetbare mijlpaal voor over 12 maanden.',
    searchTerms: ['1 jaar', 'over 1 jaar', '12 maanden'],
    aliases: ['over 1 jaar', '1 jaar', '12 maanden', 'binnen 1 jaar'],
  },
  {
    key: '6maanden',
    label: 'Over 6 maanden',
    description: 'De focus voor de eerste helft van het jaar.',
    searchTerms: ['6 maanden', 'half jaar', 'zes maanden'],
    aliases: ['over 6 maanden', '6 maanden', 'half jaar', 'zes maanden'],
  },
  {
    key: '3maanden',
    label: 'Over 3 maanden',
    description: 'Wat moet er binnen het eerste kwartaal gebeuren?',
    searchTerms: ['3 maanden', 'kwartaal', 'drie maanden'],
    aliases: ['over 3 maanden', '3 maanden', 'drie maanden', 'kwartaal'],
  },
  {
    key: 'week',
    label: 'Deze week',
    description: 'De concrete actie die je deze week uitvoert.',
    searchTerms: ['deze week', 'week', 'vandaag', 'eerstvolgende stap'],
    aliases: ['deze week', 'week', 'vandaag', 'morgen', 'deze week doe ik'],
  },
  {
    key: 'actieplan',
    label: 'Actieplan',
    description: 'Samenvatting van alle acties en de intentie om ze uit te voeren.',
    searchTerms: ['actieplan', 'samenvatting', 'acties'],
    aliases: ['actieplan', 'samenvatting', 'acties', 'volgende stap'],
  },
];

/**
 * Extract milestone data from the AI's action plan summary (step 7)
 * Returns both title and description for each milestone
 */
interface ExtractedMilestone {
  title: string;
  description: string;
  period?: string; // The timeframe text (e.g., "Over 9 jaar", "Jaar 1-2")
}

const cleanExtractedValue = (value: string): string =>
  value
    .split(/[\r\n]+/)[0]
    .replace(/^[\s:–-]+/, '')
    .replace(/[\s:–-]+$/, '')
    .trim();

const buildAliasRegex = (text: string) =>
  text
    .trim()
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\s+/g, '\\s+');

function extractMilestonesFromConversation(messages: SparringMessage[]): Record<string, ExtractedMilestone> {
  const extracted: Record<string, ExtractedMilestone> = {};
  
  const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
  if (!lastAssistantMessage) return extracted;

  const content = lastAssistantMessage.content;
  const normalizedContent = content.replace(/\r/g, '\n');
  const lines = normalizedContent
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  
  // Capture 'waarom' once for fallback descriptions
  let waaromContent = '';
  const waaromMatch = normalizedContent.match(/(?:waarom|💡\s*WAAROM)[:\s]+([^\n📅]+)/i);
  if (waaromMatch && waaromMatch[1]) {
    waaromContent = cleanExtractedValue(waaromMatch[1]);
  }

  // Capture the full summary for reference
  const fullSummary = content.trim() || 'Actieplan samenvatting uit sparring';

  // DYNAMIC EXTRACTION: Match any timeframe pattern
  // Patterns to match:
  // - 📅 Over X jaar: ...
  // - 📅 Jaar X-Y: ...
  // - 📅 Deze week: ...
  // - 📅 EINDDOEL: ...
  // - 💡 WAAROM: ...
  
  const milestoneRegex = /📅\s*([^:]+):\s*([^\n📅💡🎯⚠️]+)/gi;
  let match;
  let index = 0;
  
  while ((match = milestoneRegex.exec(normalizedContent)) !== null) {
    const timeframe = match[1].trim();
    const value = cleanExtractedValue(match[2]);
    
    if (value) {
      // Generate a unique key based on the timeframe
      let key = `dynamic_${index}`;
      
      // Map common patterns to existing keys for consistency
      if (timeframe.toLowerCase().includes('einddoel')) {
        key = 'einddoel';
      } else if (timeframe.toLowerCase().includes('deze week') || timeframe.toLowerCase().includes('week')) {
        key = 'week';
      } else if (timeframe.match(/jaar\s*\d+-\d+/i)) {
        key = `jaar_range_${index}`;
      } else if (timeframe.match(/over\s*\d+\s*jaar/i)) {
        const yearMatch = timeframe.match(/(\d+)/);
        key = yearMatch ? `jaar${yearMatch[1]}` : key;
      } else if (timeframe.match(/\d+\s*maanden/i)) {
        const monthMatch = timeframe.match(/(\d+)/);
        key = monthMatch ? `${monthMatch[1]}maanden` : key;
      }
      
      extracted[key] = {
        title: value,
        description: waaromContent || `Mijlpaal: ${timeframe}`,
        period: timeframe, // Store the actual timeframe text
      };
      index++;
    }
  }

  // Also try to extract from fixed templates as fallback
  if (Object.keys(extracted).length === 0) {
    for (const template of MILESTONE_TEMPLATES) {
      let value: string | null = null;

      // Try to find the milestone value on a single line
      for (const alias of template.aliases || []) {
        const aliasPattern = buildAliasRegex(alias);
        const lineMatch = lines.find(line =>
          line.toLowerCase().includes(alias.toLowerCase())
          && new RegExp(aliasPattern, 'i').test(line)
          && line.includes(':')
        );

        if (lineMatch) {
          const [, lineValue] = lineMatch.split(':');
          if (lineValue) {
            value = cleanExtractedValue(lineValue);
            break;
          }
        }
      }

      if (value) {
        extracted[template.key] = {
          title: value,
          description: waaromContent || `Stap richting het einddoel: ${value}`,
        };
      }
    }
  }

  // Also try to extract from user messages if we still have nothing
  if (Object.keys(extracted).length === 0) {
    for (const msg of messages.filter(m => m.role === 'user')) {
      const msgContent = msg.content.toLowerCase();
      
      for (const template of MILESTONE_TEMPLATES) {
        if (!extracted[template.key]) {
          for (const term of template.searchTerms) {
            if (msgContent.includes(term.toLowerCase())) {
              extracted[template.key] = {
                title: msg.content,
                description: waaromContent || `Mijlpaal: ${template.label}`
              };
              break;
            }
          }
        }
      }
    }
  }

  extracted['_fullSummary'] = {
    title: 'Actieplan Samenvatting',
    description: fullSummary
  };

  return extracted;
}

/**
 * SparringMilestoneCapture - Begeleide milestone capture vanuit sparring
 * 
 * Flow: 
 * 1. Toon welke mijlpaal (1 van 4) met suggestie
 * 2. Titel → Meaning (verplicht) → Global Period → Opslaan
 * 3. Na opslaan: succes + keuze voor volgende of klaar
 */
const SparringMilestoneCapture: React.FC<SparringMilestoneCaptureProps> = ({
  goalId,
  ownerId,
  sessionId,
  currentMilestoneCount,
  messages = [],
  onMilestoneCreated,
  onCancel,
  onFinish,
}) => {
  const [title, setTitle] = useState('');
  const [meaning, setMeaning] = useState('');
  const [globalPeriod, setGlobalPeriod] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedMilestoneTitle, setSavedMilestoneTitle] = useState('');
  const [hasAutoFilled, setHasAutoFilled] = useState(false);

  // Geen max limiet meer - altijd meer toevoegen toegestaan
  const canAddMore = true;
  const isValid = title.trim() !== '' && meaning.trim() !== '';
  
  // Current milestone number (1-based for display)
  const currentMilestoneNumber = currentMilestoneCount + 1;
  const currentTemplate = MILESTONE_TEMPLATES[Math.min(currentMilestoneCount, MILESTONE_TEMPLATES.length - 1)];
  
  // Extract milestone data from conversation
  const extractedData = useMemo(() => extractMilestonesFromConversation(messages), [messages]);
  
  // Auto-fill form when milestone changes
  useEffect(() => {
    if (!hasAutoFilled && currentTemplate) {
      const prefillData = extractedData[currentTemplate.key];
      
      if (prefillData) {
        setTitle(prefillData.title);
        setGlobalPeriod(currentTemplate.label);
        setMeaning(prefillData.description);
        setHasAutoFilled(true);
      }
    }
  }, [currentTemplate, extractedData, hasAutoFilled]);

  const handleSave = async () => {
    if (!isValid || isSaving || !canAddMore) return;

    setIsSaving(true);
    setError(null);

    try {
      // Create milestone via service
      const milestoneId = await createMilestone(ownerId, goalId, {
        title: title.trim(),
        targetDate: '',
        meaning: meaning.trim(),
        globalPeriod: globalPeriod.trim() || undefined,
        fromSparring: true,
        sparringSessionId: sessionId,
      });

      // Link milestone to sparring session
      await addMilestoneToSession(sessionId, milestoneId);
      
      // Generate comprehensive actions using ALL available data (non-blocking)
      // This will be done in background - milestone is already created
      (async () => {
        try {
          // Load assessment and context
          const { getLatestCompleteAssessment } = await import('../capabilities/capabilityStorage.service');
          const { generateComprehensiveActions } = await import('../actionGeneration.service');
          const { getGoalById } = await import('../firebase/goals.service');
          const { loadSelfTestResults } = await import('../assessment/selfTestStorage.service');
          
          const [completeAssessment, goalObj, selfTests] = await Promise.all([
            getLatestCompleteAssessment(ownerId, goalId),
            getGoalById(goalId),
            Promise.resolve(loadSelfTestResults()),
          ]);
          
          if (goalObj) {
            // Build milestone object
            const milestoneObj = {
              id: milestoneId,
              ownerId,
              goalId,
              title: title.trim(),
              targetDate: '',
              createdAt: Date.now(),
              meaning: meaning.trim(),
              globalPeriod: globalPeriod.trim() || undefined,
              fromSparring: true,
              sparringSessionId: sessionId,
            };
            
            // Build comprehensive context
            const actionContext = {
              goal: goalObj,
              milestone: milestoneObj as any,
              completeAssessment: completeAssessment || undefined,
              extendedDecisions: completeAssessment?.extendedDecisions,
              sparringMessages: messages || [],
              userResponses: messages?.filter(m => m.role === 'user').map(m => m.content) || [],
              selfTestResults: selfTests || undefined,
              preSparringAnswers: undefined,
              entrepreneurContext: undefined,
            };
            
            // Generate comprehensive actions (for ALL domains)
            const generatedActions = await generateComprehensiveActions(actionContext);
            
            console.log(`✅ Generated ${generatedActions.length} actions for milestone: ${title}`);
            
            // Save all actions (non-blocking)
            for (const action of generatedActions) {
              try {
                await createAction(ownerId, goalId, milestoneId, {
                  title: action.title,
                  completed: false,
                  origin: 'milestone_generated',
                  priority: action.priority,
                  actionType: action.actionType,
                  domain: action.domain, // Include domain tag
                });
              } catch (actionErr) {
                console.warn('Failed to create individual action:', actionErr);
              }
            }
            
            // Notify UI
            window.dispatchEvent(new CustomEvent('doelstellingen-actions-changed'));
          }
        } catch (actionError) {
          console.warn('Could not generate comprehensive actions (non-critical):', actionError);
          // Fallback: create simple action
          try {
            await createAction(ownerId, goalId, milestoneId, {
              title: `Werk aan ${title}`,
              completed: false,
              origin: 'milestone_generated',
            });
          } catch (fallbackErr) {
            console.error('Failed to create fallback action:', fallbackErr);
          }
        }
      })();
      
      // Log milestone creation
      await logMilestoneCreated(ownerId, goalId, milestoneId, title.trim());

      // Show success state
      setSavedMilestoneTitle(title.trim());
      setShowSuccess(true);
      setIsSaving(false);
      
      // Notify parent (updates counter)
      onMilestoneCreated(milestoneId);
    } catch (err) {
      console.error('Failed to create milestone:', err);
      setError('Kon mijlpaal niet opslaan');
      setIsSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Reset form for next milestone
    setTitle('');
    setMeaning('');
    setGlobalPeriod('');
    setShowSuccess(false);
    setSavedMilestoneTitle('');
    setHasAutoFilled(false); // Allow auto-fill for next milestone
  };

  const handleFinish = () => {
    if (onFinish) {
      onFinish();
    } else {
      onCancel();
    }
  };

  // Note: No max limit anymore - this block is kept for backwards compatibility but won't trigger

  // Success state - milestone saved
  if (showSuccess) {
    const nextMilestoneNumber = currentMilestoneCount + 1;
    const hasMoreTemplates = currentMilestoneCount < MILESTONE_TEMPLATES.length;
    const nextTemplate = hasMoreTemplates 
      ? MILESTONE_TEMPLATES[currentMilestoneCount] 
      : null;

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <SuccessIcon size={24} />
          <div className="flex-1">
            <h4 className="font-medium text-green-800">Mijlpaal opgeslagen!</h4>
            <p className="text-sm text-green-700 mt-1">
              "{savedMilestoneTitle}" is toegevoegd als mijlpaal {currentMilestoneCount}.
            </p>
            
            {/* Next suggestion with pre-filled preview */}
            {nextTemplate && (
              <div className="mt-4 p-3 bg-white/50 rounded-lg border border-green-100">
                <p className="text-sm text-green-800 font-medium">
                  <TipIcon size={14} className="inline" /> Volgende: Mijlpaal {nextMilestoneNumber} - {nextTemplate.label}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {extractedData[nextTemplate.key]?.title 
                    ? `"${extractedData[nextTemplate.key].title}"` 
                    : nextTemplate.description}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleAddAnother}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                ➕ Volgende mijlpaal
              </button>
              <button
                onClick={handleFinish}
                className="px-4 py-2 text-green-700 hover:text-green-800 text-sm"
              >
                ✓ Klaar met mijlpalen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form state - creating new milestone
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      {/* Header with milestone number and auto-fill indicator */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 font-bold text-sm">
              {currentMilestoneNumber}
            </span>
            <div>
              <h4 className="font-medium text-gray-900">
                Mijlpaal {currentMilestoneNumber}: {currentTemplate.label}
              </h4>
              <p className="text-xs text-gray-500">
                {extractedData[currentTemplate.key]?.title 
                  ? 'Automatisch ingevuld uit je actieplan' 
                  : currentTemplate.description}
              </p>
            </div>
          </div>
          <span className="text-xs text-gray-400">
            #{currentMilestoneCount + 1}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {/* Titel */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titel
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`bijv. "${currentTemplate.label}: [jouw doel]"`}
            disabled={isSaving}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
          />
        </div>

        {/* Meaning (verplicht) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Betekenis <span className="text-red-500">*</span>
          </label>
          <textarea
            value={meaning}
            onChange={(e) => setMeaning(e.target.value)}
            placeholder="Wat betekent het voor je als je dit bereikt hebt?"
            disabled={isSaving}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
          />
        </div>

        {/* Global Period - pre-filled with suggestion */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Periode
          </label>
          <input
            type="text"
            value={globalPeriod || currentTemplate.label}
            onChange={(e) => setGlobalPeriod(e.target.value)}
            placeholder={currentTemplate.label}
            disabled={isSaving}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
          {/* Left: Finish without saving this milestone */}
          <button
            onClick={handleFinish}
            disabled={isSaving}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm disabled:opacity-50"
          >
            {currentMilestoneCount > 0 
              ? `✓ Afronden met ${currentMilestoneCount} mijlpaal${currentMilestoneCount > 1 ? 'en' : ''}` 
              : '← Terug zonder mijlpalen'}
          </button>
          
          {/* Right: Save actions */}
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid || isSaving}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Opslaan...' : 'Mijlpaal opslaan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SparringMilestoneCapture;
